import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  DollarSign, Clock, Zap, CheckCircle, ArrowRight, 
  Wallet, TrendingUp, AlertCircle, Truck, FileText
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickPayPage = () => {
  const [driverEmail, setDriverEmail] = useState('');
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // New request form
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [loadId, setLoadId] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [loadDetails, setLoadDetails] = useState(null);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const res = await axios.get(`${API}/quickpay/options`);
      setOptions(res.data.options || []);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const handleLogin = async () => {
    if (!driverEmail || !driverEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/quickpay/driver/${encodeURIComponent(driverEmail)}`);
      setRequests(res.data.requests || []);
      setStats(res.data.summary || {});
      setIsLoggedIn(true);
      toast.success('Loaded your Quick Pay history');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const checkLoadEligibility = async () => {
    if (!loadId) {
      toast.error('Please enter a Load ID');
      return;
    }

    try {
      const res = await axios.get(`${API}/quickpay/load/${loadId}`);
      if (res.data.has_quickpay) {
        toast.info('Quick Pay already requested for this load');
        setLoadDetails(res.data.request);
      } else if (res.data.eligible) {
        // Get load details
        const loadRes = await axios.get(`${API}/loads/${loadId}`);
        setLoadDetails(loadRes.data);
        toast.success('Load is eligible for Quick Pay!');
      }
    } catch (error) {
      toast.error('Load not found or not eligible');
    }
  };

  const submitQuickPayRequest = async () => {
    if (!selectedOption || !loadId) {
      toast.error('Please select a payment option');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/quickpay/request`, {
        load_id: loadId,
        driver_email: driverEmail,
        driver_name: loadDetails?.driver_name || 'Driver',
        payment_option: selectedOption
      });

      toast.success(res.data.message);
      setShowRequestForm(false);
      setLoadId('');
      setSelectedOption('');
      setLoadDetails(null);
      
      // Refresh data
      handleLogin();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-400 bg-green-500/20';
      case 'approved': return 'text-blue-400 bg-blue-500/20';
      case 'processing': return 'text-amber-400 bg-amber-500/20';
      case 'pending': return 'text-orange-400 bg-orange-500/20';
      case 'rejected': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6 max-w-6xl mx-auto" data-testid="quickpay-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Quick Pay</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Get paid faster! Choose 24-hour or 48-hour payment options for completed loads.
          </p>
        </div>

        {/* Payment Options Overview */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {options.map((option) => (
            <Card 
              key={option.id} 
              className={`border-2 transition-all ${
                option.id === '24_hour' 
                  ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/50' 
                  : option.id === '48_hour'
                  ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/50'
                  : 'bg-slate-800/50 border-slate-700'
              }`}
            >
              <CardContent className="p-6 text-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  option.id === '24_hour' ? 'bg-amber-500/20' : 
                  option.id === '48_hour' ? 'bg-green-500/20' : 'bg-slate-700'
                }`}>
                  {option.id === '24_hour' ? <Zap className="w-6 h-6 text-amber-400" /> :
                   option.id === '48_hour' ? <Clock className="w-6 h-6 text-green-400" /> :
                   <DollarSign className="w-6 h-6 text-gray-400" />}
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{option.label}</h3>
                <p className="text-2xl font-bold text-white mb-2">
                  {option.fee_percentage > 0 ? `${option.fee_percentage}% Fee` : 'No Fee'}
                </p>
                <p className="text-sm text-gray-400">{option.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Login Section */}
        {!isLoggedIn ? (
          <Card className="bg-slate-800/50 border-slate-700 max-w-md mx-auto" data-testid="quickpay-login">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-green-500" />
                Access Your Quick Pay
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                type="email"
                value={driverEmail}
                onChange={(e) => setDriverEmail(e.target.value)}
                placeholder="Enter your driver email"
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="quickpay-email-input"
              />
              <Button 
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                data-testid="quickpay-login-btn"
              >
                {loading ? 'Loading...' : 'View My Quick Pay'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Stats Summary */}
            <div className="grid sm:grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <FileText className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats?.total_requests || 0}</p>
                  <p className="text-sm text-gray-400">Total Requests</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <Clock className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats?.pending || 0}</p>
                  <p className="text-sm text-gray-400">Pending</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats?.paid || 0}</p>
                  <p className="text-sm text-gray-400">Paid</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 text-center">
                  <DollarSign className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">${stats?.total_received?.toLocaleString() || 0}</p>
                  <p className="text-sm text-gray-400">Total Received</p>
                </CardContent>
              </Card>
            </div>

            {/* New Request Button */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Your Quick Pay Requests</h2>
              <Button 
                onClick={() => setShowRequestForm(!showRequestForm)}
                className="bg-gradient-to-r from-green-500 to-emerald-500"
                data-testid="new-quickpay-btn"
              >
                <Zap className="w-4 h-4 mr-2" /> New Quick Pay Request
              </Button>
            </div>

            {/* New Request Form */}
            {showRequestForm && (
              <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30">
                <CardHeader>
                  <CardTitle className="text-white">Request Quick Pay</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-4">
                    <Input
                      value={loadId}
                      onChange={(e) => setLoadId(e.target.value)}
                      placeholder="Enter Load ID"
                      className="bg-slate-700/50 border-slate-600 text-white flex-1"
                      data-testid="load-id-input"
                    />
                    <Button onClick={checkLoadEligibility} variant="outline">
                      Check Eligibility
                    </Button>
                  </div>

                  {loadDetails && !loadDetails.has_quickpay && (
                    <>
                      <div className="p-4 bg-slate-800/50 rounded-lg">
                        <p className="text-white font-medium">Load Details</p>
                        <p className="text-gray-400 text-sm">
                          {loadDetails.origin_city}, {loadDetails.origin_state} → {loadDetails.destination_city}, {loadDetails.destination_state}
                        </p>
                        <p className="text-green-400 font-bold text-lg mt-2">
                          Rate: ${loadDetails.rate?.toLocaleString() || loadDetails.accepted_rate?.toLocaleString()}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <p className="text-white font-medium">Select Payment Speed:</p>
                        <div className="grid sm:grid-cols-3 gap-3">
                          {options.map((opt) => (
                            <button
                              key={opt.id}
                              onClick={() => setSelectedOption(opt.id)}
                              className={`p-4 rounded-lg border-2 transition-all text-left ${
                                selectedOption === opt.id 
                                  ? 'border-green-500 bg-green-500/20' 
                                  : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'
                              }`}
                            >
                              <p className="text-white font-medium">{opt.label}</p>
                              <p className="text-gray-400 text-sm">{opt.fee_percentage}% fee</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      <Button 
                        onClick={submitQuickPayRequest}
                        disabled={loading || !selectedOption}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-500"
                        data-testid="submit-quickpay-btn"
                      >
                        {loading ? 'Submitting...' : 'Submit Quick Pay Request'}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Requests List */}
            <div className="space-y-4">
              {requests.length === 0 ? (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-8 text-center">
                    <Truck className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No Quick Pay requests yet</p>
                    <p className="text-sm text-gray-500">Complete a load and request Quick Pay to get paid faster!</p>
                  </CardContent>
                </Card>
              ) : (
                requests.map((request) => (
                  <Card key={request.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(request.status)}`}>
                              {request.status?.toUpperCase()}
                            </span>
                            <span className="text-gray-500 text-sm">{request.option_label}</span>
                          </div>
                          <p className="text-white font-medium">
                            {request.load_details?.origin} <ArrowRight className="inline w-4 h-4" /> {request.load_details?.destination}
                          </p>
                          <p className="text-gray-400 text-sm">Load ID: {request.load_id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-400 text-sm">Payout Amount</p>
                          <p className="text-2xl font-bold text-green-400">${request.payout_amount?.toLocaleString()}</p>
                          <p className="text-gray-500 text-xs">Fee: ${request.fee_amount} ({request.fee_percentage}%)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Change Account */}
            <div className="text-center">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setIsLoggedIn(false);
                  setRequests([]);
                  setStats(null);
                }}
                className="text-gray-400"
              >
                Switch Account
              </Button>
            </div>
          </div>
        )}

        {/* Info Section */}
        <Card className="bg-slate-800/50 border-slate-700 mt-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-500" />
              How Quick Pay Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-400 font-bold">1</span>
                </div>
                <div>
                  <h4 className="text-white font-medium">Complete a Load</h4>
                  <p className="text-sm text-gray-400">Deliver the freight and upload POD documentation</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-400 font-bold">2</span>
                </div>
                <div>
                  <h4 className="text-white font-medium">Choose Speed</h4>
                  <p className="text-sm text-gray-400">Select 24-hour (3%) or 48-hour (2%) payment</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-400 font-bold">3</span>
                </div>
                <div>
                  <h4 className="text-white font-medium">Get Paid</h4>
                  <p className="text-sm text-gray-400">Funds deposited directly to your account</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default QuickPayPage;
